import * as Yup from 'yup';
import React from 'react';
import { Formik, FormikProps } from 'formik';
import Drawer from '../Drawer';
import BuzzlUserFiltersForm from './BuzzlUserFiltersForm';
import Spinner from '../Spinner';
import { createSearchParams, useNavigate } from 'react-router-dom';
import { BuzzlUserRole } from '../../types/buzzl-user';
import { useParamsToObj } from '../../hooks/useParamsToObj';
import { buzzlRoles } from '../../utils/config';

type BuzzlUserDrawerProps = {
  onClose: () => void;
  open: boolean;
};

export type FilterData = {
  admin: buzzlRoles.admin | '';
  operator: buzzlRoles.operator | '';
  customer: string | '';
};

const schema = Yup.object().shape({
  admin: Yup.string().oneOf(['ADMIN']),
  operator: Yup.string().oneOf(['OPERATOR']),
  customer: Yup.string(),
});

const BuzzlUserFilterDrawer: React.FC<BuzzlUserDrawerProps> = ({
  onClose,
  open,
}) => {
  const navigate = useNavigate();
  const urlParams = useParamsToObj(['search', 'role', 'customer']);

  const initialValues: FilterData = {
    admin:
      (urlParams.role as BuzzlUserRole) === buzzlRoles.admin
        ? buzzlRoles.admin
        : '',
    operator:
      (urlParams.role as BuzzlUserRole) === buzzlRoles.operator
        ? buzzlRoles.operator
        : '',
    customer: urlParams.customer || '',
  };

  const handleOnSubmit = async ({ admin, operator, customer }: FilterData) => {
    try {
      const searchParams = createSearchParams();

      if (urlParams.search) searchParams.append('search', urlParams.search);
      if (admin || operator) {
        searchParams.append('role', admin || operator);
      }
      if (customer) searchParams.append('customer', customer);

      navigate({
        search: `?${searchParams}`,
      });

      onClose();
    } catch (e: any) {
      console.log(
        '🚀 ~ file: BuzzlUserFilterDrawer.tsx ~ line 52 ~ handleOnSubmit ~ error',
        e
      );
    }
  };

  return (
    <Drawer open={open} onClose={onClose}>
      <Formik
        enableReinitialize
        initialValues={initialValues}
        validationSchema={schema}
        validateOnChange={false}
        validateOnBlur={false}
        onSubmit={handleOnSubmit}
      >
        {({
          isSubmitting,
          dirty,
          setFieldValue,
          values,
        }: FormikProps<FilterData>) => (
          <Drawer.FormContainer
            open={open}
            onClose={onClose}
            title="Filters"
            Footer={
              <>
                <button
                  className="btn btn-white"
                  onClick={() => {
                    navigate({
                      search: ``,
                    });
                    onClose();
                  }}
                  disabled={isSubmitting}
                  type="button"
                >
                  Reset
                </button>

                <button
                  className="btn btn-primary ml-4"
                  type="submit"
                  disabled={isSubmitting || !dirty}
                >
                  {isSubmitting ? (
                    <>
                      <Spinner
                        className="h-5 w-5 text-gray-700"
                        aria-hidden="true"
                      />
                      Loading
                    </>
                  ) : (
                    'Apply'
                  )}
                </button>
              </>
            }
          >
            <BuzzlUserFiltersForm
              isSubmitting={isSubmitting}
              setFieldValue={setFieldValue}
              values={values}
            />
          </Drawer.FormContainer>
        )}
      </Formik>
    </Drawer>
  );
};

export default BuzzlUserFilterDrawer;
